import type { ReactNode } from 'react';

import { TextArea } from '@playbook/atoms/inputs/text-area';
import { ChoiceCard } from '@playbook/compositions/choice-card';
import { RadioGroup } from '@playbook/compositions/radio-group';

import css from './KeyMaterialChoice.module.css';

export type KeyMaterialSource = 'generate' | 'paste';

const PublicKeyMarker = '-----BEGIN PUBLIC KEY-----';

/**
 *	Whether the pasted text is the right half of the pair.
 *
 *	Only the marker. The server parses the key properly and refuses anything
 *	that is not a P-256 or RSA-2048 PEM; this catches the one mistake worth
 *	catching before it is sent — a private key in a field that transmits.
 */
export function looksLikePublicKey(pasted: string): boolean {
	return pasted.includes(PublicKeyMarker);
}

interface KeyMaterialChoiceProps {
	source: KeyMaterialSource;
	/** The pasted PEM. Ignored while `source` is `generate`. */
	publicKey: string;

	onSourceChange: (source: KeyMaterialSource) => void;
	onPublicKeyChange: (publicKey: string) => void;
}

/**
 *	Where the key pair comes from.
 *
 *	Generating is the default because it is the path that cannot go wrong: the
 *	private half is made here, shown once and never transmitted. Pasting is for
 *	a customer whose key already lives in their own secret store.
 */
export function KeyMaterialChoice({
	source,
	publicKey,

	onSourceChange,
	onPublicKeyChange,
}: KeyMaterialChoiceProps): ReactNode {
	// Only once something has been typed — an empty field is not yet wrong.
	const wrongHalf = publicKey !== '' && !looksLikePublicKey(publicKey);

	return (
		<div className={css.choice}>
			<RadioGroup name="keyMaterial" label="Key pair">
				<ChoiceCard
					name="keyMaterial"
					value="generate"
					checked={source === 'generate'}
					label="Make one for me"
					description="ECDSA P-256, generated in this browser. You download the private key on the next screen — shown once, never sent to us."
					onChange={() => onSourceChange('generate')}
				/>
				<ChoiceCard
					name="keyMaterial"
					value="paste"
					checked={source === 'paste'}
					label="I have a public key"
					description="PEM, P-256 or RSA 2048 and up."
					onChange={() => onSourceChange('paste')}
				/>
			</RadioGroup>

			{source === 'paste' && (
				<TextArea
					required
					name="publicKey"
					rows={6}
					value={publicKey}
					label="Public key"
					description="The public half only. Never paste a private key here."
					placeholder={PublicKeyMarker}
					status={wrongHalf ? 'error' : undefined}
					message={
						wrongHalf
							? `This does not contain ${PublicKeyMarker}. If it says PRIVATE KEY, that is the half to keep.`
							: undefined
					}
					onChange={(event) => onPublicKeyChange(event.target.value)}
				/>
			)}
		</div>
	);
}
